/**
 * Combined Enrolment Security Gate — VeraLok Admin queue.
 *
 * VeraLok-internal security operators (system_admin, identity_officer,
 * compliance_officer) review cases opened by the combined-risk gate:
 *
 *   HIGH      → PENDING_REVIEW  (no VLK_ID until cleared)
 *   CRITICAL  → HARD_BLOCK      (durable restriction; only Admin can
 *                                clear or confirm)
 *
 * Resolution options:
 *   • CLEARED           → legitimate applicant; future retries pass
 *                         through the gate normally.
 *   • CONFIRMED_ABUSE   → durable restriction preserved; matching
 *                         bypass keys elevate to CRITICAL on retry.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SEVERITY_STYLES = {
  HIGH:     { color: "#B45309", label: "HIGH" },
  CRITICAL: { color: "#DC2626", label: "CRITICAL" },
};
const STATUS_STYLES = {
  PENDING:         { color: "#B45309", label: "Pending" },
  CLEARED:         { color: "#059669", label: "Cleared" },
  CONFIRMED_ABUSE: { color: "#DC2626", label: "Confirmed Abuse" },
};

const STATUS_FILTERS = ["PENDING", "CLEARED", "CONFIRMED_ABUSE", "ALL"];

export default function EnrolmentSecurity() {
  const [status, setStatus] = useState("PENDING");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/enrolment-security/cases?status=${status}`);
      setItems(r.data?.items ?? []);
    } catch (e) {
      toast.error("Could not load enrolment security queue.");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (nextStatus) => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(
        `/admin/enrolment-security/cases/${selected.case_id}/resolve`,
        { status: nextStatus, note: note.trim() || null },
      );
      toast.success(`Case ${nextStatus === "CLEARED" ? "cleared" : "restricted"}.`);
      setSelected(null); setNote("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Could not resolve case.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1240, margin: "0 auto" }} data-testid="enrolment-security-page">
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Enrolment Security</h1>
        <div style={{ display: "flex", gap: 8 }}>
          {STATUS_FILTERS.map(f => (
            <button
              key={f}
              onClick={() => setStatus(f)}
              data-testid={`filter-${f.toLowerCase()}`}
              style={{
                padding: "6px 12px",
                borderRadius: 8,
                border: "1px solid #E5E7EB",
                background: status === f ? "#111827" : "#FFF",
                color: status === f ? "#FFF" : "#111827",
                cursor: "pointer",
                fontSize: 13, fontWeight: 500,
              }}
            >{f.replace("_", " ")}</button>
          ))}
        </div>
      </div>

      <p style={{ color: "#6B7280", fontSize: 13, marginBottom: 18 }}>
        Cases opened by the combined enrolment security gate. Signal-safety
        rules ensure no single IP/geo event caused these; each carries at
        least one independent evidence source.
      </p>

      {loading ? (
        <div style={{ color: "#6B7280", padding: 24 }} data-testid="loading">Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ color: "#6B7280", padding: 24, textAlign: "center" }} data-testid="empty-state">
          No cases in this state.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(360px, 1fr) 2fr", gap: 20 }}>
          <div>
            {items.map(c => (
              <CaseRow key={c.case_id} c={c} selected={selected?.case_id === c.case_id} onClick={() => setSelected(c)} />
            ))}
          </div>
          <div>
            {selected ? (
              <CaseDetail
                c={selected}
                note={note}
                setNote={setNote}
                busy={busy}
                onResolve={resolve}
              />
            ) : (
              <div style={{ color: "#6B7280", padding: 24, textAlign: "center", border: "1px dashed #E5E7EB", borderRadius: 12 }}>
                Select a case to review the evidence.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CaseRow({ c, selected, onClick }) {
  const sev = SEVERITY_STYLES[c.severity] || { color: "#6B7280", label: c.severity };
  const st = STATUS_STYLES[c.status] || { color: "#6B7280", label: c.status };
  const created = new Date(c.created_at).toLocaleString();
  return (
    <div
      onClick={onClick}
      data-testid={`case-row-${c.case_id}`}
      style={{
        padding: 14,
        border: `1px solid ${selected ? "#111827" : "#E5E7EB"}`,
        borderRadius: 12,
        background: selected ? "#F9FAFB" : "#FFF",
        cursor: "pointer",
        marginBottom: 10,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>{c.case_id}</span>
        <span style={{ color: sev.color, fontWeight: 700, fontSize: 12 }}>{sev.label}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", color: "#6B7280", fontSize: 12 }}>
        <span>{st.label}</span>
        <span>{created}</span>
      </div>
      <div style={{ marginTop: 6, fontSize: 12 }}>
        <span style={{ color: "#374151" }}>
          {c.reason_codes?.slice(0, 3).join(", ")}
          {c.reason_codes?.length > 3 && ` +${c.reason_codes.length - 3}`}
        </span>
      </div>
    </div>
  );
}

function CaseDetail({ c, note, setNote, busy, onResolve }) {
  const sev = SEVERITY_STYLES[c.severity] || { color: "#6B7280", label: c.severity };
  const st = STATUS_STYLES[c.status] || { color: "#6B7280", label: c.status };
  const attempts = c.attempts || [];
  const signals = c.signals || {};
  const canResolve = c.status === "PENDING";
  const showAllReasons = useMemo(() => (c.reason_codes || []).join(", "), [c.reason_codes]);
  return (
    <div style={{ padding: 20, border: "1px solid #E5E7EB", borderRadius: 12, background: "#FFF" }} data-testid="case-detail">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{c.case_id}</div>
          <div style={{ color: "#6B7280", fontSize: 12 }}>Opened {new Date(c.created_at).toLocaleString()}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <span style={{ background: sev.color, color: "#FFF", padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 700 }}>{sev.label}</span>
          <span style={{ background: st.color, color: "#FFF", padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 700 }}>{st.label}</span>
        </div>
      </div>

      <Section label="Reason codes"><code style={{ fontSize: 12 }}>{showAllReasons || "—"}</code></Section>

      <Section label="Contributing sources">
        {(signals.contributing_sources || []).map(s => (
          <span key={s} style={{ display: "inline-block", background: "#F3F4F6", padding: "3px 8px", borderRadius: 6, fontSize: 11, marginRight: 4 }}>{s}</span>
        ))}
      </Section>

      <Section label="Signal safety notes">
        {(signals.signal_safety_notes || []).length === 0 ? (
          <span style={{ color: "#6B7280", fontSize: 12 }}>—</span>
        ) : (signals.signal_safety_notes || []).map(n => (
          <div key={n} style={{ fontSize: 12, color: "#374151" }}>• {n}</div>
        ))}
      </Section>

      <Section label="System failures (never contributed)">
        {(signals.system_failure_reasons || []).length === 0 ? (
          <span style={{ color: "#6B7280", fontSize: 12 }}>—</span>
        ) : (signals.system_failure_reasons || []).map(r => (
          <div key={r} style={{ fontSize: 12, color: "#6B7280" }}>• {r}</div>
        ))}
      </Section>

      <Section label={`Retry attempts (${attempts.length})`}>
        {attempts.slice(-6).reverse().map((a, i) => (
          <div key={i} style={{ fontSize: 12, color: "#374151", padding: "4px 0", borderBottom: i === Math.min(6, attempts.length) - 1 ? "none" : "1px dashed #E5E7EB" }}>
            <span>{new Date(a.ts).toLocaleString()}</span>
            <span style={{ color: "#6B7280", marginLeft: 8 }}>
              via {(a.source_keys || []).join(",")} · {a.severity}
            </span>
          </div>
        ))}
      </Section>

      <Section label="Liveness sub-signals">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 12 }}>
          {Object.entries(signals.liveness_sub_signals || {}).map(([k, v]) => (
            <div key={k} style={{ padding: "4px 8px", background: "#F9FAFB", borderRadius: 6 }}>
              <div style={{ fontWeight: 600 }}>{k}</div>
              <div style={{ color: "#6B7280" }}>{v?.reason_code || "OK"}</div>
            </div>
          ))}
        </div>
      </Section>

      {canResolve && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #E5E7EB" }}>
          <textarea
            data-testid="resolution-note"
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Optional resolution note (visible in audit log)"
            style={{ width: "100%", padding: 10, border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, minHeight: 60, marginBottom: 10 }}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <button
              data-testid="resolve-cleared"
              onClick={() => onResolve("CLEARED")}
              disabled={busy}
              style={{ flex: 1, padding: 10, background: "#059669", color: "#FFF", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}
            >Clear as legitimate</button>
            <button
              data-testid="resolve-confirmed"
              onClick={() => onResolve("CONFIRMED_ABUSE")}
              disabled={busy}
              style={{ flex: 1, padding: 10, background: "#DC2626", color: "#FFF", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}
            >Confirm abuse / restrict</button>
          </div>
        </div>
      )}
      {!canResolve && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #E5E7EB", fontSize: 12, color: "#6B7280" }}>
          Resolved {c.resolved_at ? new Date(c.resolved_at).toLocaleString() : "—"}
          {c.resolution_note ? ` · "${c.resolution_note}"` : ""}
        </div>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4, fontWeight: 600 }}>{label}</div>
      <div>{children}</div>
    </div>
  );
}
